import { DeleteOutlined, MinusOutlined, PlusOutlined, ShoppingCartOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Button, InputNumber, Space, Dropdown, Badge, Tooltip } from 'antd';
import React, { FC, useState, useEffect } from 'react';
import { useDebounce } from 'use-debounce';

import type { CardBlockProps } from './types';
import { MediaImage } from '../visual/shared';
import {
  CatalogueOfferPrice,
} from '../visual/offerPresentation';
import { getVariantPriceRange } from '@eshop/components/catalogue/Cataloguefunctions.tsx';

interface DesignConfig {
  mediaWrapper: 'button' | 'div';
  addButtonType: 'default' | 'primary';
  addButtonClassName?: string;
  cartTagClassName: string;
  cartTagColor?: string;
  cartFooterClassName: string;
}

const CARD_DESIGN_CONFIG: Record<string, DesignConfig> = {
  minimalista: {
    mediaWrapper: 'button',
    addButtonType: 'default',
    addButtonClassName: 'catalogue-card__cta',
    cartTagClassName: 'catalogue-card__tag',
    cartTagColor: 'var(--catalogue-accent)',
    cartFooterClassName: 'catalogue-card__footer',
  },
  moderno: {
    mediaWrapper: 'div',
    addButtonType: 'primary',
    cartTagClassName: 'catalogue-card__tag',
    cartFooterClassName: 'catalogue-card__footer',
  },
};

const getDesignConfig = (design: string | undefined): DesignConfig => {
  const base = CARD_DESIGN_CONFIG[design ?? ''];
  return (
    base ?? {
      mediaWrapper: 'div',
      addButtonType: 'primary',
      cartTagClassName: 'catalogue-card__tag',
      cartFooterClassName: 'catalogue-card__footer',
    }
  );
};

export const resolveSelectedVariant = (
  item: CardBlockProps['item'],
  variantState?: CardBlockProps['variantState'],
): CardBlockProps['item'] | null => {
  return (
    variantState?.selectedVariant
    ?? item.children?.find((child) => Number(child.id) === Number(variantState?.selectedVariantId))
    ?? null
  ) as CardBlockProps['item'] | null;
};

const resolveMediaItem = (
  item: CardBlockProps['item'],
  variantState?: CardBlockProps['variantState'],
): CardBlockProps['item'] => {
  const selectedVariant = resolveSelectedVariant(item, variantState);
  if (selectedVariant?.images?.length) {
    return selectedVariant;
  }

  if (item.images?.length) {
    return item;
  }

  const firstVariantWithImage = item.children?.find((child) => child.images?.length);
  return firstVariantWithImage ? (firstVariantWithImage as CardBlockProps['item']) : item;
};

interface HighlightsOverlayProps {
  highlightState: CardBlockProps['highlightState'];
}

const HighlightsOverlay: FC<HighlightsOverlayProps> = ({ highlightState }) => {
  if (!highlightState) {
    return null;
  }

  return (
    <>
      {highlightState.label && (
        <span className="catalogue-card__chip">{highlightState.label}</span>
      )}
      {highlightState.isSoldOut && (
        <span className="catalogue-card__chip catalogue-card__chip--soldout">Sin stock</span>
      )}
      {highlightState.isNew && (
        <span className="catalogue-card__chip catalogue-card__chip--new">Nuevo</span>
      )}
    </>
  );
};

export const MediaBlock: FC<CardBlockProps> = ({
  design,
  item,
  index,
  mediaAspectRatio,
  interactions,
  highlightState,
  priorityImageCount,
  variantState,
}) => {
  const config = getDesignConfig(design);
  const viewDetail = () => interactions?.onViewDetail?.(item);
  const overlay = <HighlightsOverlay highlightState={highlightState} />;
  const mediaItem = resolveMediaItem(item, variantState);
  const priorityThreshold = typeof priorityImageCount === 'number' ? priorityImageCount : 0;
  const isPriorityImage = priorityThreshold > 0 && typeof index === 'number' && index < priorityThreshold;
  const mediaLoading = isPriorityImage ? 'eager' : 'lazy';
  const mediaFetchPriority = isPriorityImage ? 'high' : 'auto';
  const mediaDecoding = isPriorityImage ? 'sync' : 'async';

  if (config.mediaWrapper === 'button') {
    return (
      <button
        type="button"
        className="catalogue-card__thumbnail"
        onClick={viewDetail}
        aria-label={`Ver detalles de ${item.name}`}
      >
        <MediaImage
          item={mediaItem}
          aspect={mediaAspectRatio}
          loading={mediaLoading}
          decoding={mediaDecoding}
          fetchPriority={mediaFetchPriority}
          variant="card"
        />
        {overlay}
      </button>
    );
  }

  return (
    <div className="catalogue-card__thumbnail" role="presentation" onClick={viewDetail}>
      <MediaImage
        item={mediaItem}
        aspect={mediaAspectRatio}
        loading={mediaLoading}
        decoding={mediaDecoding}
        fetchPriority={mediaFetchPriority}
        variant="card"
      />
      {overlay}
    </div>
  );
};

export const TitleBlock: FC<CardBlockProps> = ({ item, interactions }) => {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!interactions?.onViewDetail) {
      return;
    }

    event.preventDefault();
    interactions.onViewDetail(item);
  };

  return (
    <a href={item.url} className="catalogue-card__title" onClick={handleClick}>
      {item.name}
    </a>
  );
};

export const PriceBlock: FC<CardBlockProps> = ({ item, variantState }) => {
  const selectedVariant = resolveSelectedVariant(item, variantState);

  if (!selectedVariant) {
    const priceRange = getVariantPriceRange(item);
    if (priceRange) {
      return (
        <div className="catalogue-card__pricing">
          <span className="catalogue-card__price catalogue-card__price-range">
            {priceRange.formattedRange}
          </span>
        </div>
      );
    }
  }

  const displayItem = (selectedVariant ? { ...item, ...selectedVariant } : item) as typeof item;
  return <CatalogueOfferPrice item={displayItem} />;
};

export const StockBadgeBlock: FC<CardBlockProps> = ({ item }) => {
  const isAvailable = item.stock_state !== 'out_of_stock' && item.disponibilidad !== false;

  if (!isAvailable) {
    return null;
  }

  return <span className="catalogue-card__stock">En stock</span>;
};

/*
export const HighlightsBlock: FC<CardBlockProps> = ({ cartState, design }) => {
  if (!cartState?.isInCart) {
    return null;
  }

  const config = getDesignConfig(design);
  const quantityLabel =
    typeof cartState.quantity === 'number' ? ` · ${cartState.quantity}` : '';

  return (
    <Tag className={config.cartTagClassName} color={config.cartTagColor}>
      {`En el carrito${quantityLabel}`}
    </Tag>
  );
};
*/

export const useCartActionsBlocks = ({
  cartState,
  interactions,
  item,
  variantState,
}: CardBlockProps): React.ReactNode[] => {
  const [localQuantity, setLocalQuantity] = useState<number | undefined>(
    typeof cartState?.quantity === 'number' ? cartState.quantity : undefined
  );
  const [debouncedQuantity] = useDebounce(localQuantity, 500);

  // Sincroniza la cantidad local con la respuesta de la API cuando deja de actualizar
  useEffect(() => {
    if (!cartState?.isUpdating) {
      if (typeof cartState?.quantity === 'number') {
        setLocalQuantity(cartState.quantity);
      } else {
        setLocalQuantity(undefined);
      }
    }
  }, [cartState?.isUpdating, cartState?.quantity]);

  // Solo hace un llamado a la API cuando el valor debounced ha cambiado
  useEffect(() => {
    if (
      typeof debouncedQuantity === 'number' &&
      typeof cartState?.quantity === 'number' &&
      debouncedQuantity !== cartState.quantity
    ) {
      interactions?.onChangeQuantity?.(item, debouncedQuantity);
    }
  }, [debouncedQuantity]);

  const quantityValue =
    typeof localQuantity === 'number' ? localQuantity : (typeof cartState?.quantity === 'number' ? cartState.quantity : undefined);
  const minQuantity = cartState?.minQuantity ?? 1;
  const isAtMinimumQuantity =
    typeof quantityValue === 'number' && quantityValue <= minQuantity;
  const shouldShowDelete = Boolean(cartState?.isInCart && isAtMinimumQuantity);
  const disableDecrease =
    cartState?.isUpdating ||
    typeof quantityValue !== 'number' ||
    cartState?.canDecrease === false;
  const disableDelete = cartState?.isUpdating || cartState?.canDelete === false;
  const disableIncrease =
    cartState?.isUpdating ||
    cartState?.canIncrease === false ||
    (typeof cartState?.maxQuantity === 'number' &&
      typeof quantityValue === 'number' &&
      quantityValue >= cartState.maxQuantity);

  const handleAdd = () => interactions?.onAddToCart?.(item);
  const handleIncrease = () => {
    setLocalQuantity((prev) => {
      const current = typeof prev === 'number' ? prev : (cartState?.quantity ?? 0);
      const next = current + 1;
      const max = cartState?.maxQuantity;
      return typeof max === 'number' && next > max ? max : next;
    });
  };
  const handleDecrease = () => {
    setLocalQuantity((prev) => {
      const current = typeof prev === 'number' ? prev : (cartState?.quantity ?? minQuantity);
      const next = current - 1;
      return next >= 0 ? next : 0;
    });
  };
  const handleRemove = () => {
    if (interactions?.onRemoveItem) {
      interactions.onRemoveItem(item);
      return;
    }
    handleDecrease();
  };
  const handleManualQuantity = (value: number | null) => {
    if (typeof value === 'number' && value >= 0) {
      setLocalQuantity(value);
    }
  };

  const hasVariants = Boolean(variantState?.hasVariants);

  const selectedOption = variantState?.options.find(
    (o) => o.id === variantState.selectedVariantId
  );

  // Total in cart for this product class
  const totalVariantsInCart = variantState?.options.reduce((sum, opt) => sum + (opt.inCartQuantity || 0), 0) || 0;

  // Extraer el nombre corto de la variante removiendo el nombre del item padre
  const getShortName = (fullName: string, parentName: string) => {
    let shortName = fullName;
    if (fullName.startsWith(parentName)) {
      shortName = fullName.substring(parentName.length);
      // Limpiar prefijos o sufijos comunes como guiones, paréntesis, espacios extras
      shortName = shortName.replace(/^[\s(\-\/)]+|[\s)\-\/]+$/g, '');
    }
    return shortName || fullName;
  };

  const selectedShortName = selectedOption ? getShortName(selectedOption.label, item.name) : undefined;

  const variantSelector = hasVariants ? (
    <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', display: 'flex', /*height: '2rem'*/ }}>
      <Badge
        count={totalVariantsInCart}
        color="red"
        className="site-badge-count-4"
        styles={{ indicator: { zIndex: 1 }, root: { width: '100%', display: 'flex', height: '100%' } }}
      >
        <Dropdown
          trigger={['click']}
          menu={{
            items: variantState!.options.map((option) => {
              const shortOptName = getShortName(option.label, item.name);
              return {
                key: String(option.id),
                disabled: option.disabled,
                onClick: (e: any) => {
                  e.domEvent.stopPropagation();
                  variantState!.onSelect?.(option.id);
                },
                label: (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
                    <span>{shortOptName}</span>
                    {option.inCartQuantity && option.inCartQuantity > 0 ? (
                      <Badge count={option.inCartQuantity} style={{ backgroundColor: '#52c41a' }} />
                    ) : null}
                  </div>
                ),
              };
            }),
          }}
        >
          <Button
            className="catalogue-card__variant-select"
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
          >
            <span
              className={`text-truncate ${selectedOption?.disabled ? "text-danger text-decoration-line-through" : ""}`}
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {selectedShortName || "Seleccione uno"}
            </span>
            {selectedOption && (
              <CloseCircleOutlined
                style={{ marginLeft: 8 }}
                onClick={(e) => {
                  e.stopPropagation();
                  variantState!.onSelect?.(undefined);
                }}
              />
            )}
          </Button>
        </Dropdown>
      </Badge>
    </div>
  ) : null;

  const requiresVariantSelection = hasVariants && !selectedOption;
  const disabledAdd = cartState?.isUpdating || requiresVariantSelection;

  const cartControl = cartState?.isInCart ? (
    <Space.Compact className="catalogue-card__quantity">
      <Button
        // size="small"
        icon={shouldShowDelete ? <DeleteOutlined /> : <MinusOutlined />}
        onClick={shouldShowDelete ? handleRemove : handleDecrease}
        disabled={shouldShowDelete ? disableDelete : disableDecrease}
        danger={shouldShowDelete}
        aria-label={shouldShowDelete ? 'Eliminar del carrito' : 'Disminuir cantidad'}
      />
      <InputNumber
        // size="small"
        value={quantityValue}
        min={minQuantity}
        max={cartState?.maxQuantity}
        onChange={handleManualQuantity}
        disabled={cartState?.isUpdating}
        controls={false}
        className="catalogue-card__quantity-input"
        aria-label="Cantidad de producto"
      />
      <Button
        // size="small"
        icon={<PlusOutlined />}
        onClick={handleIncrease}
        disabled={disableIncrease}
        aria-label="Aumentar cantidad"
      />
    </Space.Compact>
  ) : (
    requiresVariantSelection ? (
      <Tooltip title="Debes elegir una opción" color="red">
        <div style={{ display: 'inline-block', width: '100%', height: '100%' }}>
          <Button
            className="catalogue-card__cart-btn"
            icon={<ShoppingCartOutlined style={{ fontSize: '1.8rem' }} />}
            disabled
            style={{ width: '100%', height: '100%', pointerEvents: 'none' }}
            aria-label="Agregar al carrito"
          />
        </div>
      </Tooltip>
    ) : (
      <Button
        className="catalogue-card__cart-btn"
        icon={<ShoppingCartOutlined style={{ fontSize: '1.8rem' }} />}
        onClick={handleAdd}
        disabled={disabledAdd}
        style={{ width: '100%', height: '100%' }}
        aria-label="Agregar al carrito"
      />
    )
  );

  if (hasVariants) {
    return [variantSelector, cartControl];
  }

  return [cartControl];
};

export const CartActionsBlock = useCartActionsBlocks;

/*
export const CartActionsBlock: FC<CardBlockProps> = (props) => {
  const { design } = props;
  const config = getDesignConfig(design);
  const actionsArray = useCartActionsBlocks(props);
  const hasVariants = Boolean(props.variantState?.hasVariants);

  if (hasVariants) {
    return (
      <div className={config.cartFooterClassName}>
        <div className="catalogue-card__footer-row">
          {actionsArray[0]}
          <div className="catalogue-card__footer-action">
            {actionsArray[1]}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={config.cartFooterClassName}>
      <div className="catalogue-card__footer-centered">
        {actionsArray[0]}
      </div>
    </div>
  );
};
*/
